import {
  compact,
  curry,
  filter,
  isEmpty,
  isNil,
  map,
  startCase,
  toLower,
  trim,
} from "lodash/fp";
import { DateTime } from "luxon";

import { getItemAndIndexAtTimestamp } from "@app/analytics/api/models";
import { SUPPRESSED_SPEAKER_NAMES } from "@app/analytics/MeetingReport/constants";
import type { TranscriptItem } from "@app/analytics/MeetingReport/types";
import type {
  BaseTranscriptTurn,
  ReactionHighlight,
  SessionChapter,
  SessionNote,
  TranscriptSpeaker,
  TranscriptSummary,
  TranscriptSummaryItem,
  TranscriptTurnParagraph,
} from "@app/common/types";
import { formatTimeTick, toFormattedStringList } from "@app/common/utils";

export const areSpeakersEqual = curry(
  (speakerA: TranscriptSpeaker, speakerB: TranscriptSpeaker) =>
    speakerA.name === speakerB.name
);

export const isTurnParagraph = (
  item?: TranscriptItem
): item is TranscriptTurnParagraph => item != null && "turn" in item;

export const filterTranscriptBySpeakers = (
  transcriptItems: TranscriptItem[],
  speakersToExclude: TranscriptSpeaker[]
): TranscriptItem[] => {
  if (isEmpty(speakersToExclude)) return transcriptItems;

  const speakerNamesToExclude = map("name", speakersToExclude);

  return filter(
    (item) =>
      !isTurnParagraph(item) ||
      (item.turn.speaker && !speakerNamesToExclude.includes(item.turn.speaker)),
    transcriptItems
  ) as TranscriptItem[];
};

export const getTimestamp = (turnStartTime: DateTime, startTime: DateTime) => {
  const relativeTime = turnStartTime.diff(startTime!).toMillis();
  return formatTimeTick(relativeTime > 0 ? relativeTime : 0);
};

const getSectionAsString = (
  sectionTitle: string,
  items: string[],
  isNotetakerReport = false,
  options: Parameters<typeof toFormattedStringList>[1] = { bulleted: true }
) => {
  const useEmptyContent = isEmpty(compact(items));
  const isNotesSection = sectionTitle === "Notepad";
  const populatedItems = useEmptyContent
    ? [
        `No ${isNotesSection ? "notes" : toLower(startCase(sectionTitle))}${
          isNotetakerReport || isNotesSection ? "" : " generated"
        }`,
      ]
    : items;

  const listString = toFormattedStringList(populatedItems, options);
  return `${sectionTitle}:\n\n${listString}`;
};

const getChapterTopicsAsString = (chapter: SessionChapter) => {
  if (!chapter.topics || isEmpty(compact(chapter.topics))) {
    return null;
  }

  return toFormattedStringList(map("value", chapter.topics), {
    bulleted: true,
  });
};

const getChaptersAsString = (chapters: SessionChapter[]): string => {
  const chapterStrings = chapters.map((chapter) => {
    return compact([
      chapter.title,
      chapter.description,
      getChapterTopicsAsString(chapter),
    ]).join("\n");
  });
  return getSectionAsString("Chapters & Topics", chapterStrings, false, {
    bulleted: false,
    lineBreak: true,
  });
};

export const getSummaryCopyContent = (
  { summary, topics, actionItems, keyQuestions, chapters }: TranscriptSummary,
  notes: SessionNote[],
  isNotetakerReport: boolean
) =>
  compact([
    isNotetakerReport
      ? null
      : getSectionAsString(
          "Summary",
          [trim(summary.value)],
          isNotetakerReport,
          {
            bulleted: false,
          }
        ),
    chapters.length
      ? getChaptersAsString(chapters)
      : getSectionAsString("Topics", map("value", topics), isNotetakerReport),
    getSectionAsString(
      "Action Items",
      map("value", actionItems),
      isNotetakerReport
    ),
    getSectionAsString(
      "Key Questions",
      map("value", keyQuestions),
      isNotetakerReport
    ),
    getSectionAsString("Notepad", map("value", notes), isNotetakerReport),
  ]).join("\n\n\n");

export const getChaptersAndTopicsCopyContent = ({
  chapters,
  topics,
}: Pick<TranscriptSummary, "chapters" | "topics">) =>
  chapters.length
    ? getChaptersAsString(chapters)
    : getSectionAsString("Topics", map("value", topics));

export const getMeetingSummaryCopyContent = ({
  summary,
}: {
  summary: TranscriptSummaryItem;
}) =>
  getSectionAsString("Summary", [trim(summary.value)], false, {
    bulleted: false,
  });

export const getNoteSectionCopyContent = ({
  name,
  data,
}: {
  name: string;
  data: (TranscriptSummaryItem | SessionNote)[];
}) => getSectionAsString(name, map("value", data));

export const generateTextFileFromSummary = (
  summary: TranscriptSummary,
  notes: SessionNote[],
  meetingDetails: {
    title?: string;
    startTime?: DateTime;
    isNotetakerReport: boolean;
  }
) => {
  const content = [];
  if (meetingDetails) {
    const details = `${
      meetingDetails.title
    } \n${meetingDetails.startTime?.toFormat("EEE. MMM d, y")}`;
    content.push(details);
  }
  content.push(
    getSummaryCopyContent(summary, notes, meetingDetails.isNotetakerReport)
  );
  return encodeURIComponent(content.join("\n\n"));
};

export const generateTextFileFromTranscript = (
  transcript: BaseTranscriptTurn[] | undefined,
  meetingDetails: {
    title: string | undefined;
    startTime: DateTime | undefined;
  }
): string => {
  if (!transcript || !meetingDetails.startTime)
    return encodeURIComponent("No data for transcript");

  const prettyTranscript = transcript.map((turn: BaseTranscriptTurn) => {
    return `${getTimestamp(turn.rawStartTime, meetingDetails.startTime!)} - ${
      turn.speaker ?? "Unidentified Speaker"
    } \r${turn.text}`;
  });

  if (meetingDetails) {
    const details = `${
      meetingDetails.title
    } \n${meetingDetails.startTime?.toFormat("EEE. MMM d, y")}`;
    prettyTranscript.unshift(details);
  }
  return encodeURIComponent(prettyTranscript.join("\n\n"));
};

export const getReactionsByParagraphId = (reactions: ReactionHighlight[]) => {
  const reactionsByTurn = new Map<string, ReactionHighlight[]>();
  reactions.forEach((reaction) => {
    reaction.paragraphs.forEach((p) => {
      const currentReactionsForTurn = reactionsByTurn.get(p.id) ?? [];
      reactionsByTurn.set(p.id, [...currentReactionsForTurn, reaction]);
    });
  });
  return reactionsByTurn;
};

const SPEAKER_REGEX = /^Speaker \d/;
const SPEAKER_REPLACE_REGEX = /^Speaker /;

export const isAnonymousSpeaker = (name: string | null): boolean => {
  return (
    isNil(name) ||
    Boolean(name.match(SPEAKER_REGEX)) ||
    SUPPRESSED_SPEAKER_NAMES.includes(name)
  );
};

export const getSpeakerSortValue = (name: string | null) => {
  if (name == null || SUPPRESSED_SPEAKER_NAMES.includes(name)) return null;
  if (isAnonymousSpeaker(name)) {
    return Number(trim(name.replace(SPEAKER_REPLACE_REGEX, "")));
  }
  return toLower(name);
};

export const generateAnonymousName = (speakers: (string | null)[]) => {
  if (isEmpty(speakers)) return "Speaker 1";
  const existingNumbers = speakers
    .map((name) => name?.match(SPEAKER_REGEX))
    .filter((match) => match !== null)
    .map((match) => {
      const num = match![0].replace(SPEAKER_REPLACE_REGEX, "");
      return parseInt(num, 10);
    });

  let newNumber = 1;
  while (existingNumbers.includes(newNumber)) {
    newNumber++;
  }

  return `Speaker ${newNumber}`;
};

export const getParagraphAtTimestamp = (
  paragraphs: TranscriptTurnParagraph[],
  timestamp: number
): TranscriptTurnParagraph | undefined => {
  const { item: paragraph } = getItemAndIndexAtTimestamp(paragraphs, timestamp);
  return paragraph;
};
